import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Business as IceIcon,
} from '@mui/icons-material';
import {
  FactureProforma,
  FactureProformaCreate,
  FactureProformaUpdate,
  FactureProformaItemCreate,
  FactureProformaStatus,
} from '../../types/factureProforma';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { Client } from '../../types/client';
import { Article } from '../../types';
import { ConditionPaiement } from '../../types/conditionPaiement';
import { ModePaiement } from '../../types/modePaiement';
import {
  facturesProformaAPI,
  clientsAPI,
  articlesAPI,
  conditionsPaiementAPI,
  modesPaiementAPI,
} from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface LineItem {
  article_id: number | null | undefined;
  designation: string;
  quantite: number | null | string;
  prix_unitaire: number | null | string;
  total: number;
  article?: Article;
}

const TVA_OPTIONS = [
  { label: '0%', value: 0 },
  { label: '7%', value: 7 },
  { label: '10%', value: 10 },
  { label: '14%', value: 14 },
  { label: '20%', value: 20 },
];

interface FactureProformaFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  proformaId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const FactureProformaFormDialog: React.FC<FactureProformaFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  proformaId,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!proformaId;

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [conditionsPaiement, setConditionsPaiement] = useState<ConditionPaiement[]>([]);
  const [modesPaiement, setModesPaiement] = useState<ModePaiement[]>([]);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<ConditionPaiement | null>(null);
  const [selectedMode, setSelectedMode] = useState<ModePaiement | null>(null);

  const [formData, setFormData] = useState<{
    date: string;
    tva_rate: number;
    delai_realisation: number | null | string;
    remarque: string;
    status: string;
  }>({
    date: new Date().toISOString().split('T')[0],
    tva_rate: 20,
    delai_realisation: null,
    remarque: '',
    status: FactureProformaStatus.BROUILLON as string,
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([
    { article_id: null, designation: '', quantite: null, prix_unitaire: null, total: 0 },
  ]);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new proformas (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    selectedClient: Client | null;
    selectedCondition: ConditionPaiement | null;
    selectedMode: ModePaiement | null;
    lineItems: LineItem[];
  }>('facture-proforma', !isEdit);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      tva_rate: 20,
      delai_realisation: null,
      remarque: '',
      status: FactureProformaStatus.BROUILLON as string,
    });
    setSelectedClient(null);
    setSelectedCondition(null);
    setSelectedMode(null);
    setLineItems([{ article_id: null, designation: '', quantite: null, prix_unitaire: null, total: 0 }]);
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const [clientsData, articlesData, conditionsData, modesData] = await Promise.all([
            clientsAPI.getAllForDropdown(),
            articlesAPI.getAllForDropdown(),
            conditionsPaiementAPI.getAll(),
            modesPaiementAPI.getAll(),
          ]);
          setClients(clientsData);
          setArticles(articlesData);
          setConditionsPaiement(conditionsData);
          setModesPaiement(modesData);

          if (isEdit && proformaId) {
            const proforma = await facturesProformaAPI.getById(proformaId);
            populateForm(proforma, clientsData, conditionsData, modesData);
          } else {
            const draft = loadDraft();
            if (draft) {
              setFormData(draft.formData);
              setSelectedClient(draft.selectedClient);
              setSelectedCondition(draft.selectedCondition);
              setSelectedMode(draft.selectedMode);
              setLineItems(draft.lineItems);
            } else {
              resetForm();
            }
          }
        } catch (err) {
          showError('Erreur lors du chargement des données');
          console.error(err);
        }
      };
      fetchData();
    }
  }, [open, proformaId, isEdit]);

  const populateForm = (
    proforma: FactureProforma,
    clientsList: Client[],
    conditionsList: ConditionPaiement[],
    modesList: ModePaiement[]
  ) => {
    setFormData({
      date: proforma.date,
      tva_rate: Number(proforma.tva_rate),
      delai_realisation: proforma.delai_realisation || null,
      remarque: proforma.remarque || '',
      status: proforma.status,
    });

    const client = clientsList.find((c) => c.id === proforma.client_id);
    setSelectedClient(client || null);

    const condition = conditionsList.find((c) => c.id === proforma.condition_paiement_id);
    setSelectedCondition(condition || null);

    const mode = modesList.find((m) => m.id === proforma.mode_paiement_id);
    setSelectedMode(mode || null);

    if (proforma.items && proforma.items.length > 0) {
      setLineItems(
        proforma.items.map((item) => ({
          article_id: item.article_id,
          designation: item.designation,
          quantite: typeof item.quantite === 'string' ? parseFloat(item.quantite) : item.quantite,
          prix_unitaire: typeof item.prix_unitaire === 'string' ? parseFloat(item.prix_unitaire) : item.prix_unitaire,
          total: typeof item.total === 'string' ? parseFloat(item.total) : item.total,
        }))
      );
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newItems = [...lineItems];
    if (article) {
      newItems[index] = {
        ...newItems[index],
        article_id: article.id,
        designation: article.designation,
        article,
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        article_id: null,
        designation: '',
        article: undefined,
      };
    }
    setLineItems(newItems);
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: any) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], [field]: value };

    if (field === 'quantite' || field === 'prix_unitaire') {
      const quantiteValue = newItems[index].quantite;
      const quantite = typeof quantiteValue === 'string'
        ? parseFloat(quantiteValue.replace(',', '.')) || 0
        : (quantiteValue ?? 0);

      const prixValue = newItems[index].prix_unitaire;
      const prix = typeof prixValue === 'string'
        ? parseFloat(prixValue.replace(',', '.')) || 0
        : (prixValue ?? 0);

      newItems[index].total = Math.round(quantite * prix * 100) / 100;
    }

    setLineItems(newItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { article_id: null, designation: '', quantite: null, prix_unitaire: null, total: 0 },
    ]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotalHT = () => {
    const total = lineItems.reduce((sum, item) => {
      const t = typeof item.total === 'string' ? parseFloat(item.total) || 0 : (item.total || 0);
      return sum + t;
    }, 0);
    return Math.round(total * 100) / 100;
  };

  const calculateTVA = () => {
    const totalHT = calculateTotalHT();
    return Math.round(totalHT * (formData.tva_rate / 100) * 100) / 100;
  };

  const calculateTotalTTC = () => {
    return Math.round((calculateTotalHT() + calculateTVA()) * 100) / 100;
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      showError('Veuillez sélectionner un client');
      return;
    }

    const validItems = lineItems.filter((item) => {
      const quantite = typeof item.quantite === 'string'
        ? parseFloat(item.quantite.replace(',', '.'))
        : item.quantite;
      return item.designation && item.designation.trim() !== '' && quantite && quantite > 0;
    });

    if (validItems.length === 0) {
      showError('Veuillez ajouter au moins un article');
      return;
    }

    try {
      setLoading(true);

      const items: FactureProformaItemCreate[] = validItems.map((item) => {
        const quantite = typeof item.quantite === 'string'
          ? parseFloat(item.quantite.replace(',', '.')) || 0
          : (item.quantite ?? 0);
        const prixUnitaire = typeof item.prix_unitaire === 'string'
          ? parseFloat(item.prix_unitaire.replace(',', '.')) || 0
          : (item.prix_unitaire ?? 0);

        return {
          article_id: item.article_id ?? null,
          designation: item.designation,
          quantite: Math.round(quantite * 100) / 100,
          prix_unitaire: Math.round(prixUnitaire * 100) / 100,
          total: Math.round(item.total * 100) / 100,
        };
      });

      if (isEdit && proformaId) {
        const updateData: FactureProformaUpdate = {
          date: formData.date,
          client_id: selectedClient.id,
          tva_rate: formData.tva_rate,
          condition_paiement_id: selectedCondition ? selectedCondition.id : null,
          mode_paiement_id: selectedMode ? selectedMode.id : null,
          delai_realisation: formData.delai_realisation ? Number(formData.delai_realisation) : null,
          remarque: formData.remarque || null,
          status: formData.status,
          items,
        };
        await facturesProformaAPI.update(proformaId, updateData);
        showSuccess('Facture proforma modifiée avec succès');
      } else {
        const createData: FactureProformaCreate = {
          date: formData.date,
          client_id: selectedClient.id,
          tva_rate: formData.tva_rate,
          condition_paiement_id: selectedCondition ? selectedCondition.id : null,
          mode_paiement_id: selectedMode ? selectedMode.id : null,
          delai_realisation: formData.delai_realisation ? Number(formData.delai_realisation) : null,
          remarque: formData.remarque || null,
          items,
        };
        await facturesProformaAPI.create(createData);
        showSuccess('Facture proforma créée avec succès');
      }

      clearDraft();
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData, selectedClient, selectedCondition, selectedMode, lineItems });
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={SlideUpTransition}
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              {isEdit ? 'Modifier la Facture Proforma' : 'Nouvelle Facture Proforma'}
            </Typography>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="#4CAF50" onClick={minimize} />
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations Générales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Client" required size="small" />
                )}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>TVA (%)</InputLabel>
                <Select
                  value={formData.tva_rate}
                  label="TVA (%)"
                  onChange={(e) => setFormData({ ...formData, tva_rate: Number(e.target.value) })}
                >
                  {TVA_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {selectedClient && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(76, 175, 80, 0.3)',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1, fontWeight: 600 }}>
                Informations du Client
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2">
                      <strong>ICE:</strong> {selectedClient.ice ? selectedClient.ice : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2">
                      {selectedClient.name ? selectedClient.name : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <Typography variant="body2">
                      {selectedClient.address ? selectedClient.address : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {isEdit && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value={FactureProformaStatus.BROUILLON}>Brouillon</MenuItem>
                    <MenuItem value={FactureProformaStatus.VALIDEE}>Validée</MenuItem>
                    <MenuItem value={FactureProformaStatus.ANNULEE}>Annulée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Articles
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addLineItem}
              sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
            >
              Ajouter Article
            </Button>
          </Box>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: '35%' }}>Désignation</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                  <TableCell sx={{ width: '20%' }} align="right">Prix Unitaire</TableCell>
                  <TableCell sx={{ width: '20%' }} align="right">Total</TableCell>
                  <TableCell sx={{ width: '10%' }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={articles}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return `${option.reference} - ${option.designation}`;
                        }}
                        value={
                          item.article_id
                            ? articles.find((a) => a.id === item.article_id) || null
                            : item.designation || ''
                        }
                        onChange={(_, value) => {
                          if (typeof value === 'string') {
                            handleItemChange(index, 'designation', value);
                            handleItemChange(index, 'article_id', null);
                          } else if (value) {
                            handleArticleChange(index, value);
                          } else {
                            handleItemChange(index, 'designation', '');
                            handleItemChange(index, 'article_id', null);
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = (e.target as HTMLInputElement).value;
                          if (inputValue && !item.article_id) {
                            handleItemChange(index, 'designation', inputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            multiline
                            rows={3}
                            placeholder="Sélectionner ou saisir une désignation"
                            sx={{
                              '& .MuiInputBase-root': {
                                alignItems: 'flex-start',
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.reference} - {option.designation}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.quantite ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'quantite', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'quantite', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const numValue = parseFloat(value.replace(',', '.'));
                            if (!isNaN(numValue)) {
                              handleItemChange(index, 'quantite', numValue);
                            } else {
                              handleItemChange(index, 'quantite', null);
                            }
                          }
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.prix_unitaire ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'prix_unitaire', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'prix_unitaire', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const numValue = parseFloat(value.replace(',', '.'));
                            if (!isNaN(numValue)) {
                              handleItemChange(index, 'prix_unitaire', numValue);
                            } else {
                              handleItemChange(index, 'prix_unitaire', null);
                            }
                          }
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="right">
                      {item.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
                Conditions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={conditionsPaiement}
                    getOptionLabel={(option) => option.nom}
                    value={selectedCondition}
                    onChange={(_, value) => setSelectedCondition(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Condition de Paiement" size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={modesPaiement}
                    getOptionLabel={(option) => option.nom}
                    value={selectedMode}
                    onChange={(_, value) => setSelectedMode(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Mode de Paiement" size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Délai de réalisation (jours)"
                    type="number"
                    fullWidth
                    size="small"
                    value={formData.delai_realisation ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setFormData({ ...formData, delai_realisation: null });
                      } else {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setFormData({ ...formData, delai_realisation: numValue });
                        }
                      }
                    }}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Remarque"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={formData.remarque}
                    onChange={(e) => setFormData({ ...formData, remarque: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
                Totaux
              </Typography>
              <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Total HT:</Typography>
                  <Typography variant="body2">
                    {calculateTotalHT().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">TVA ({formData.tva_rate}%):</Typography>
                  <Typography variant="body2">
                    {calculateTVA().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop="2px solid #4CAF50">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Total TTC:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} color="#4CAF50">
                    {calculateTotalTTC().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            {loading ? 'Sauvegarde...' : isEdit ? 'Modifier' : 'Créer la Facture Proforma'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#4CAF50"
        title={isEdit ? 'Modifier la Facture Proforma' : 'Nouvelle Facture Proforma'}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default FactureProformaFormDialog;
